import { useState } from 'react';
import { Button, Field, Modal, Select } from '@bb/ui';
import { formatCount } from '../../audience/ChannelReachBreakdown.tsx';

export const EXIT_TARGET = 'exit';

export interface MigrationMapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Steps of the running version that the update removes while entries are in them (`requiredMigrations`). */
  required: Array<{ stepId: string; entries: number }>;
  /** Names of the running version's steps: the removed ones exist only there. */
  removedNames: Record<string, string>;
  /** Steps of the new version, the possible targets. */
  targets: Array<{ id: string; name: string }>;
  busy?: boolean;
  error?: string | null;
  /** Publish with `migrate` and this map (every removed step → a new step or `exit`). */
  onMigrate: (map: Record<string, string>) => void;
  /** Publish with `pin`: running entries finish on their version. */
  onPin: () => void;
}

/**
 * Updating a running Canvas whose new version removes steps that still hold entries (BB-110, BB-121): each such step
 * is mapped to a step of the new version or to «exit» before entries move (`migrate`); keeping running entries on
 * their version (`pin`) is the alternative.
 */
export function MigrationMapDialog({ open, onOpenChange, required, removedNames, targets, busy = false, error = null, onMigrate, onPin }: MigrationMapDialogProps) {
  const [map, setMap] = useState<Record<string, string>>({});
  const complete = required.every((r) => map[r.stepId]);
  const options = [...targets.map((t) => ({ value: t.id, label: t.name })), { value: EXIT_TARGET, label: 'Exit the Canvas' }];
  const total = required.reduce((n, r) => n + r.entries, 0);
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) setMap({});
        onOpenChange(next);
      }}
      title="Where do running entries go?"
      description={`This update removes ${required.length === 1 ? 'a step' : 'steps'} where ${formatCount(total)} ${total === 1 ? 'user is' : 'users are'} right now.`}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={onPin}>
            Keep running entries on the current version
          </Button>
          <Button variant="primary" disabled={!complete || busy} loading={busy} onClick={() => onMigrate(Object.fromEntries(required.map((r) => [r.stepId, map[r.stepId]!])))}>
            Update and move entries
          </Button>
        </>
      }
    >
      <p className="hint">
        Moving takes every running entry to the new version: users on steps that stay continue there, users on a removed step go to the step you choose, or leave the Canvas. Keeping them on the current version lets them finish the journey they started; only new entries get the update.
      </p>
      <ul className="plain-list" aria-label="Removed steps with entries">
        {required.map((r) => {
          const name = removedNames[r.stepId] ?? r.stepId;
          return (
            <li key={r.stepId}>
              <Field label={`«${name}»: ${formatCount(r.entries)} ${r.entries === 1 ? 'entry' : 'entries'} go to`}>
                <Select placeholder="Choose a step or exit" options={options} value={map[r.stepId] ?? ''} disabled={busy} onChange={(e) => setMap((m) => ({ ...m, [r.stepId]: e.target.value }))} />
              </Field>
            </li>
          );
        })}
      </ul>
      {!complete && <p className="hint">Choose where the entries of every removed step go to move them.</p>}
      {error && <p role="alert">{error}</p>}
    </Modal>
  );
}
